import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ref,
  get,
  onValue,
  query,
  equalTo,
  orderByChild,
} from "firebase/database";

import { db, auth } from "./../firebase_config";
import { enqueueSnackbar } from "./notifications";
import errorReporter from "../utils/errorReporting";
import { decoratePayments } from "../utils/payments";

const FUNCTIONS_BASE = "https://us-central1-shelby-estates-hoa.cloudfunctions.net";

// Calls a Cloud Function as the signed-in user. Resolves with the parsed
// JSON body; rejects with an Error carrying the server's message otherwise.
const callFunction = async (name, { method = "POST", body } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Please sign in first");
  const idToken = await user.getIdToken();
  const response = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || `${name} failed (${response.status})`);
  }
  return data;
};

export const processSquarePayment = createAsyncThunk(
  "payment/processSquarePayment",
  async (
    { sourceId, amount, currency, locationId, note, customerId },
    { rejectWithValue },
  ) => {
    try {
      // The function mirrors the new payment into squareTransactions itself,
      // so the live subscriptions below pick it up without a refetch.
      return await callFunction("processSquarePayment", {
        body: { sourceId, amount, currency, locationId, customerId, note },
      });
    } catch (error) {
      errorReporter.reportPaymentError(error, "process_payment", {
        amount: String(amount),
      });
      console.error("Error in processSquarePayment:", error);
      return rejectWithValue(error.message || "Payment processing failed");
    }
  },
);

export const postSinglePayment = createAsyncThunk(
  "payment/postSinglePayment",
  async (transactionId, { rejectWithValue }) => {
    try {
      return await callFunction("postSinglePayment", {
        body: { transactionId },
      });
    } catch (error) {
      errorReporter.reportPaymentError(error, "manual_post", {
        transaction_id: transactionId,
      });
      console.error("Error in postSinglePayment:", error);
      return rejectWithValue(error.message || "Payment posting failed");
    }
  },
);

// ---------------------------------------------------------------------------
// Live feeds
//
// The payment screens subscribe to Realtime Database feeds rather than
// fetching once, so posting activity from the auto-poster shows up without a
// refresh. Unsubscribe handles live here (not in Redux state, which must stay
// serializable) and are torn down when the screen unmounts or resubscribes.
// ---------------------------------------------------------------------------

let adminUnsubscribes = [];
let customerUnsubscribe = null;

const stopAdminFeeds = () => {
  adminUnsubscribes.forEach((unsubscribe) => unsubscribe());
  adminUnsubscribes = [];
};

const stopCustomerFeed = () => {
  if (customerUnsubscribe) customerUnsubscribe();
  customerUnsubscribe = null;
};

// Pulls the latest payments from Square into squareTransactions (admin).
// The live feeds then deliver the new rows; this thunk only reports whether
// the sync itself succeeded.
export const syncSquarePayments = createAsyncThunk(
  "payment/syncSquarePayments",
  async (_, { rejectWithValue }) => {
    try {
      return await callFunction("fetchAllTransactions", { method: "GET" });
    } catch (error) {
      errorReporter.reportAPIError(error, "fetchAllTransactions", "GET");
      console.error("Error syncing Square payments:", error);
      return rejectWithValue(error.message || "Failed to sync payments");
    }
  },
);

// Admin: every Square payment with resident names and live posted flags.
export const subscribePayments = createAsyncThunk(
  "payment/subscribePayments",
  async (_, { dispatch, rejectWithValue }) => {
    stopAdminFeeds();
    try {
      // Names change rarely; one read is enough for the life of the screen.
      const users = await get(ref(db, "users")).then((s) => s.val() || {});

      let transactions = {};
      let postedPayments = {};
      const publish = () =>
        dispatch(listPayments(decoratePayments(transactions, postedPayments, users)));

      const onError = (error) => {
        console.error("Payments feed error:", error);
        dispatch(setPaymentsError(error.message || "Lost connection to payments"));
      };

      adminUnsubscribes = [
        onValue(
          ref(db, "squareTransactions"),
          (snapshot) => {
            transactions = snapshot.val() || {};
            publish();
          },
          onError,
        ),
        onValue(
          ref(db, "postedPayments"),
          (snapshot) => {
            postedPayments = snapshot.val() || {};
            publish();
          },
          onError,
        ),
      ];
      return null;
    } catch (error) {
      console.error("Error subscribing to payments:", error);
      return rejectWithValue(error.message || "Failed to load payments");
    }
  },
);

export const unsubscribePayments = createAsyncThunk(
  "payment/unsubscribePayments",
  async () => {
    stopAdminFeeds();
    return null;
  },
);

// Resident: only their own Square payments. The database rules allow this
// query solely when it is scoped to the caller's own customer id.
export const subscribeCustomerPayments = createAsyncThunk(
  "payment/subscribeCustomerPayments",
  async (customerId, { dispatch, rejectWithValue }) => {
    stopCustomerFeed();
    if (!customerId) {
      dispatch(listPayments({}));
      return null;
    }
    try {
      customerUnsubscribe = onValue(
        query(
          ref(db, "squareTransactions"),
          orderByChild("customerId"),
          equalTo(customerId),
        ),
        (snapshot) => dispatch(listPayments(snapshot.val() || {})),
        (error) => {
          console.error("Customer payments feed error:", error);
          dispatch(setPaymentsError(error.message || "Failed to load payments"));
        },
      );
      return null;
    } catch (error) {
      console.error("Error subscribing to customer payments:", error);
      return rejectWithValue(error.message || "Failed to load payments");
    }
  },
);

export const unsubscribeCustomerPayments = createAsyncThunk(
  "payment/unsubscribeCustomerPayments",
  async () => {
    stopCustomerFeed();
    return null;
  },
);

// ---------------------------------------------------------------------------
// Auto-posting controls (admin)
// ---------------------------------------------------------------------------

export const enableAutoPosting = createAsyncThunk(
  "payment/enableAutoPosting",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const data = await callFunction("enableAutoPosting");
      dispatch(
        enqueueSnackbar({
          message:
            "Auto-posting enabled. Completed transactions will be posted automatically.",
          options: { variant: "success" },
        }),
      );
      return data;
    } catch (error) {
      errorReporter.reportError(error, {
        component: "payments",
        action: "enable_auto_posting",
        severity: "high",
      });
      console.error("Error enabling auto-posting:", error);
      return rejectWithValue(error.message || "Failed to enable auto-posting");
    }
  },
);

export const disableAutoPosting = createAsyncThunk(
  "payment/disableAutoPosting",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const data = await callFunction("disableAutoPosting");
      dispatch(
        enqueueSnackbar({
          message: "Auto-posting disabled. Manual posting is now required.",
          options: { variant: "info" },
        }),
      );
      return data;
    } catch (error) {
      errorReporter.reportError(error, {
        component: "payments",
        action: "disable_auto_posting",
        severity: "medium",
      });
      console.error("Error disabling auto-posting:", error);
      return rejectWithValue(error.message || "Failed to disable auto-posting");
    }
  },
);

export const batchPostPayments = createAsyncThunk(
  "payment/batchPostPayments",
  async (transactionIds, { dispatch, rejectWithValue }) => {
    try {
      const data = await callFunction("batchPostPayments", {
        body: { transactionIds },
      });
      dispatch(
        enqueueSnackbar({
          message: `Posted ${data.postedCount} transactions.`,
          options: { variant: "success" },
        }),
      );
      return data;
    } catch (error) {
      errorReporter.reportPaymentError(error, "batch_post", {
        transaction_count: transactionIds?.length || 0,
      });
      console.error("Error batch posting payments:", error);
      return rejectWithValue(error.message || "Failed to batch post payments");
    }
  },
);

export const checkAutoPostingStatus = createAsyncThunk(
  "payment/checkAutoPostingStatus",
  async (_, { rejectWithValue }) => {
    try {
      return await callFunction("checkAutoPostingStatus", { method: "GET" });
    } catch (error) {
      // Background check; not worth an alert
      console.warn("Error checking auto-posting status:", error);
      return rejectWithValue("Failed to check auto-posting status");
    }
  },
);

const paymentSlice = createSlice({
  name: "payment",
  initialState: {
    loading: false,
    syncing: false,
    error: null,
    lastPaymentId: null,
    payments: {},
    autoPostingEnabled: false,
    autoPostingStatus: null,
    batchOperations: {
      posting: false,
      lastBatchResult: null,
    },
  },
  reducers: {
    listPayments: (state, action) => {
      state.error = null;
      state.payments = action.payload || {};
    },
    setPaymentsError: (state, action) => {
      state.error = action.payload;
    },
    clearPaymentError: (state) => {
      state.error = null;
    },
    clearTxnId: (state) => {
      state.lastPaymentId = null;
    },
    setAutoPostingStatus: (state, action) => {
      state.autoPostingEnabled = action.payload.enabled;
      state.autoPostingStatus = action.payload.status;
    },
    clearBatchResult: (state) => {
      state.batchOperations.lastBatchResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(processSquarePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processSquarePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.lastPaymentId = action.payload.paymentId;
      })
      .addCase(processSquarePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(postSinglePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postSinglePayment.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(postSinglePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(syncSquarePayments.pending, (state) => {
        state.syncing = true;
      })
      .addCase(syncSquarePayments.fulfilled, (state) => {
        state.syncing = false;
      })
      .addCase(syncSquarePayments.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload;
      })
      .addCase(subscribePayments.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(subscribeCustomerPayments.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(enableAutoPosting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enableAutoPosting.fulfilled, (state, action) => {
        state.loading = false;
        state.autoPostingEnabled = true;
        state.autoPostingStatus = action.payload.status;
      })
      .addCase(enableAutoPosting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(disableAutoPosting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disableAutoPosting.fulfilled, (state, action) => {
        state.loading = false;
        state.autoPostingEnabled = false;
        state.autoPostingStatus = action.payload.status;
      })
      .addCase(disableAutoPosting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(batchPostPayments.pending, (state) => {
        state.batchOperations.posting = true;
        state.error = null;
      })
      .addCase(batchPostPayments.fulfilled, (state, action) => {
        state.batchOperations.posting = false;
        state.batchOperations.lastBatchResult = action.payload;
      })
      .addCase(batchPostPayments.rejected, (state, action) => {
        state.batchOperations.posting = false;
        state.error = action.payload;
      })
      .addCase(checkAutoPostingStatus.fulfilled, (state, action) => {
        state.autoPostingEnabled = action.payload.enabled;
        state.autoPostingStatus = action.payload.status;
      });
  },
});

export const {
  listPayments,
  setPaymentsError,
  clearPaymentError,
  clearTxnId,
  setAutoPostingStatus,
  clearBatchResult,
} = paymentSlice.actions;

export default paymentSlice.reducer;
